/**
 * WeaveIn.ai Platform - Households API Service
 * TypeScript service layer for household management in DCM programs.
 * Manages households, household members, individual needs, follow-ups, and services.
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type DisplacementStatus = 'DISPLACED' | 'TEMPORARY_HOUSING' | 'PERMANENT_REHOUSED' | 'RETURNED';
export type HouseholdStatus = 'ACTIVE' | 'CLOSED' | 'ARCHIVED';
export type PropertyType = 'OWNED' | 'RENTED' | 'OTHER';
export type HouseholdRole = 'HEAD_OF_HOUSEHOLD' | 'SPOUSE' | 'CHILD' | 'DEPENDENT' | 'OTHER';
export type MemberStatus = 'ACTIVE' | 'INACTIVE' | 'RELOCATED';
export type ServiceStatus = 'IDENTIFIED' | 'IN_PROGRESS' | 'COMPLETED' | 'DENIED' | 'CANCELLED';
export type NeedStatus = 'IDENTIFIED' | 'IN_PROGRESS' | 'REFERRED' | 'RESOLVED';
export type NeedPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
export type FollowUpStatus = 'SCHEDULED' | 'COMPLETED' | 'MISSED' | 'RESCHEDULED' | 'CANCELLED';
export type ContactMethod = 'PHONE' | 'IN_PERSON' | 'VIDEO' | 'EMAIL' | 'HOME_VISIT' | 'OTHER';

export type NeedType =
  | 'SCHOOL_SUPPLIES_TEXTBOOKS'
  | 'WORK_UNIFORMS_SAFETY_GEAR'
  | 'MEDICAL_EQUIPMENT_DEVICES'
  | 'PRESCRIPTION_MEDICATIONS_GLASSES'
  | 'PERSONAL_DOCUMENTS'
  | 'MOBILITY_AIDS'
  | 'ACCESS_FUNCTIONAL_NEEDS'
  | 'CHILDRENS_CLOTHING_ESSENTIALS'
  | 'PERSONAL_ELECTRONICS'
  | 'HOUSING'
  | 'TRANSPORTATION'
  | 'FOOD_ASSISTANCE'
  | 'MENTAL_HEALTH'
  | 'EMPLOYMENT'
  | 'CHILDCARE'
  | 'LEGAL_SERVICES'
  | 'FINANCIAL_ASSISTANCE'
  | 'OTHER';

export type PathwayType =
  | 'UNMET_NEEDS_GRANT'
  | 'REBUILD'
  | 'VOLUNTEER_SERVICE_REQUEST'
  | 'REFERRAL_TO_EXTERNAL_AGENCY'
  | 'CASE_CLOSURE';

export const NEED_TYPE_LABELS: Record<NeedType, string> = {
  SCHOOL_SUPPLIES_TEXTBOOKS: 'School Supplies / Textbooks',
  WORK_UNIFORMS_SAFETY_GEAR: 'Work Uniforms / Safety Gear',
  MEDICAL_EQUIPMENT_DEVICES: 'Medical Equipment / Devices',
  PRESCRIPTION_MEDICATIONS_GLASSES: 'Prescription Medications / Glasses',
  PERSONAL_DOCUMENTS: 'Personal Documents',
  MOBILITY_AIDS: 'Mobility Aids',
  ACCESS_FUNCTIONAL_NEEDS: 'Access/Functional Needs',
  CHILDRENS_CLOTHING_ESSENTIALS: "Children's Clothing / Essentials",
  PERSONAL_ELECTRONICS: 'Personal Electronics',
  HOUSING: 'Housing',
  TRANSPORTATION: 'Transportation',
  FOOD_ASSISTANCE: 'Food Assistance',
  MENTAL_HEALTH: 'Mental Health',
  EMPLOYMENT: 'Employment',
  CHILDCARE: 'Childcare',
  LEGAL_SERVICES: 'Legal Services',
  FINANCIAL_ASSISTANCE: 'Financial Assistance',
  OTHER: 'Other',
};

export const PATHWAY_LABELS: Record<PathwayType, string> = {
  UNMET_NEEDS_GRANT: 'Unmet Needs Grant',
  REBUILD: 'Rebuild',
  VOLUNTEER_SERVICE_REQUEST: 'Volunteer Service Request',
  REFERRAL_TO_EXTERNAL_AGENCY: 'Referral to External Agency',
  CASE_CLOSURE: 'Case Closure',
};

export const NEED_STATUS_LABELS: Record<NeedStatus, string> = {
  IDENTIFIED: 'Identified',
  IN_PROGRESS: 'In Progress',
  REFERRED: 'Referred',
  RESOLVED: 'Resolved',
};

export interface HouseholdNeed {
  need_type: string;
  description: string;
  status: string;
  priority: string;
}

export interface IndividualNeed {
  need_type: string;
  description: string;
  status: string;
  priority: string;
  amount?: number;
}

export interface Household {
  id: number;
  cbo_id: number;
  program_id?: number;
  household_number: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  county?: string;
  property_type?: PropertyType;
  had_insurance?: boolean;
  insurance_type?: string;
  insurance_provider?: string;
  insurance_policy_number?: string;
  disaster_event?: string;
  disaster_date?: string;
  displacement_date?: string;
  displacement_status?: DisplacementStatus;
  household_needs: HouseholdNeed[];
  status: HouseholdStatus;
  case_opened_date?: string;
  case_closed_date?: string;
  case_closed_reason?: string;
  assigned_to_user_id?: number;
  assigned_to_name?: string;
  created_at?: string;
  updated_at?: string;
  members?: HouseholdMember[];
  member_count?: number;
  /** Configured (custom) household field values — {field_name: value}. See TRUS-379. */
  custom_fields?: Record<string, any>;
}

export interface HouseholdCreate {
  cbo_id: number;
  program_id?: number;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  county?: string;
  property_type?: PropertyType;
  had_insurance?: boolean;
  insurance_type?: string;
  insurance_provider?: string;
  insurance_policy_number?: string;
  disaster_event?: string;
  disaster_date?: string;
  displacement_date?: string;
  displacement_status?: DisplacementStatus;
  household_needs?: HouseholdNeed[];
  /** Configured (custom) field values scoped to households — {field_name: value}.
   *  Rendered from com_fields where applies_to_record_types contains 'household'
   *  and persisted to household_forms by the backend. See TRUS-379. */
  custom_fields?: Record<string, any>;
}

export interface HouseholdUpdate {
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  county?: string;
  property_type?: PropertyType;
  had_insurance?: boolean;
  insurance_type?: string;
  insurance_provider?: string;
  insurance_policy_number?: string;
  disaster_event?: string;
  disaster_date?: string;
  displacement_date?: string;
  displacement_status?: DisplacementStatus;
  household_needs?: HouseholdNeed[];
  status?: HouseholdStatus;
  case_closed_reason?: string;
  assigned_to_user_id?: number;
  /** Configured (custom) household field values — {field_name: value}. See TRUS-379. */
  custom_fields?: Record<string, any>;
}

export interface HouseholdMember {
  id: number;
  household_id: number;
  member_id: number;
  role_in_household: HouseholdRole;
  relationship_to_head?: string;
  current_address_line1?: string;
  current_city?: string;
  current_state?: string;
  current_zip?: string;
  current_phone?: string;
  current_email?: string;
  is_displaced: boolean;
  individual_needs: IndividualNeed[];
  status: MemberStatus;
  joined_date?: string;
  left_date?: string;
  left_reason?: string;
  // Joined fields from members table
  first_name?: string;
  last_name?: string;
  member_email?: string;
  member_phone?: string;
}

export interface HouseholdMemberAdd {
  member_id: number;
  role_in_household: HouseholdRole;
  relationship_to_head?: string;
  current_address_line1?: string;
  current_city?: string;
  current_state?: string;
  current_zip?: string;
  current_phone?: string;
  current_email?: string;
  is_displaced?: boolean;
  individual_needs?: IndividualNeed[];
}

export interface HouseholdMemberUpdate {
  role_in_household?: HouseholdRole;
  relationship_to_head?: string;
  current_address_line1?: string;
  current_city?: string;
  current_state?: string;
  current_zip?: string;
  current_phone?: string;
  current_email?: string;
  is_displaced?: boolean;
  individual_needs?: IndividualNeed[];
  status?: MemberStatus;
  left_reason?: string;
}

export interface HouseholdService {
  id: number;
  household_id: number;
  household_member_id?: number;
  service_type: string;
  service_description?: string;
  service_provider?: string;
  service_status: ServiceStatus;
  funding_source?: string;
  amount_requested?: number;
  amount_approved?: number;
  amount_disbursed?: number;
  requested_date?: string;
  approved_date?: string;
  completed_date?: string;
  notes?: string;
  related_need_id?: number;
  created_at?: string;
  service_for?: string;
  member_first_name?: string;
  member_last_name?: string;
}

export interface HouseholdServiceCreate {
  household_member_id?: number;
  service_type: string;
  service_description?: string;
  service_provider?: string;
  service_status?: ServiceStatus;
  funding_source?: string;
  amount_requested?: number;
  amount_approved?: number;
  amount_disbursed?: number;
  requested_date?: string;
  notes?: string;
  related_need_id?: number;
}

export interface HouseholdServiceUpdate {
  service_type?: string;
  service_description?: string;
  service_provider?: string;
  service_status?: ServiceStatus;
  funding_source?: string;
  amount_requested?: number;
  amount_approved?: number;
  amount_disbursed?: number;
  approved_date?: string;
  completed_date?: string;
  notes?: string;
}

// Survivor Needs — can be scoped to an individual (household_member_id set)
// or to the whole household (household_member_id null). `scope` is returned
// by the backend as a convenience derived from household_member_id.
export interface SurvivorNeed {
  id: number;
  household_member_id: number | null;
  household_id: number;
  need_type: NeedType;
  need_type_other?: string;
  need_notes?: string;
  priority: NeedPriority;
  need_status: NeedStatus;
  status_changed_at?: string;
  related_service_id?: number;
  created_at?: string;
  updated_at?: string;
  survivor_name?: string | null;
  member_first_name?: string | null;
  member_last_name?: string | null;
  scope?: 'INDIVIDUAL' | 'HOUSEHOLD';
}

export interface SurvivorNeedCreate {
  /** Omit (or null) for a household-scoped need. */
  household_member_id?: number | null;
  need_type: NeedType;
  need_type_other?: string;
  need_notes?: string;
  priority?: NeedPriority;
  need_status?: NeedStatus;
  related_service_id?: number;
}

export interface SurvivorNeedUpdate {
  need_type?: NeedType;
  need_type_other?: string;
  need_notes?: string;
  priority?: NeedPriority;
  need_status?: NeedStatus;
  related_service_id?: number;
}

// Follow-ups / Next Steps (Phase 2)
export interface FollowUp {
  id: number;
  household_id: number;
  household_member_id?: number;
  next_contact_date: string;
  next_contact_time?: string;
  contact_location?: string;
  contact_method?: ContactMethod;
  pathways_identified: PathwayType[];
  notes?: string;
  status: FollowUpStatus;
  completed_at?: string;
  completed_notes?: string;
  created_at?: string;
  follow_up_for?: string;
  /** TRUS-697: the IANA zone next_contact_date / _time are in (default: the scheduler's). */
  timezone?: string | null;
}

export interface FollowUpCreate {
  household_member_id?: number;
  next_contact_date: string;
  next_contact_time?: string;
  contact_location?: string;
  contact_method?: ContactMethod;
  pathways_identified?: PathwayType[];
  notes?: string;
  /** TRUS-697: the zone the date / time are in (default: the scheduler's own). */
  timezone?: string;
}

export interface FollowUpUpdate {
  next_contact_date?: string;
  next_contact_time?: string;
  contact_location?: string;
  contact_method?: ContactMethod;
  pathways_identified?: PathwayType[];
  notes?: string;
  status?: FollowUpStatus;
  completed_notes?: string;
}

export interface TimelineEntry {
  type: string;
  id: number;
  title: string;
  description?: string;
  subject: string;
  date: string;
}

// ============================================================================
// API CLIENT
// ============================================================================

class HouseholdsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('Households API Error:', {
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          message: error.response?.data?.detail || error.message,
        });
        return Promise.reject(error);
      }
    );
  }

  // ==========================================================================
  // HOUSEHOLD CRUD
  // ==========================================================================

  async createHousehold(data: HouseholdCreate): Promise<{ id: number; household_number: string }> {
    const res = await this.client.post('/api/households', data);
    return res.data;
  }

  async listHouseholds(params: {
    program_id?: number;
    status?: HouseholdStatus;
    search?: string;
  }): Promise<Household[]> {
    const res = await this.client.get('/api/households', { params });
    return res.data;
  }

  async getHousehold(householdId: number): Promise<Household> {
    const res = await this.client.get(`/api/households/${householdId}`);
    return res.data;
  }

  async updateHousehold(householdId: number, data: HouseholdUpdate): Promise<{ status: string }> {
    const res = await this.client.put(`/api/households/${householdId}`, data);
    return res.data;
  }

  // ==========================================================================
  // HOUSEHOLD MEMBERS
  // ==========================================================================

  async addMember(householdId: number, data: HouseholdMemberAdd): Promise<{ id: number; status: string }> {
    const res = await this.client.post(`/api/households/${householdId}/members`, data);
    return res.data;
  }

  async batchAddMembers(householdId: number, members: HouseholdMemberAdd[]): Promise<{ created_ids: number[]; count: number }> {
    const res = await this.client.post(`/api/households/${householdId}/members/batch`, { members });
    return res.data;
  }

  async updateMember(
    householdId: number,
    memberId: number,
    data: HouseholdMemberUpdate
  ): Promise<{ status: string }> {
    const res = await this.client.put(
      `/api/households/${householdId}/members/${memberId}`,
      data
    );
    return res.data;
  }

  async removeMember(householdId: number, memberId: number): Promise<{ status: string }> {
    const res = await this.client.delete(
      `/api/households/${householdId}/members/${memberId}`
    );
    return res.data;
  }

  async linkMemberToFamily(householdId: number, memberId: number): Promise<{ family_member_id?: number; status: string }> {
    const res = await this.client.post(
      `/api/households/${householdId}/members/${memberId}/link-family`
    );
    return res.data;
  }

  // ==========================================================================
  // HOUSEHOLD SERVICES
  // ==========================================================================

  async addService(householdId: number, data: HouseholdServiceCreate): Promise<{ id: number; status: string }> {
    const res = await this.client.post(`/api/households/${householdId}/services`, data);
    return res.data;
  }

  async listServices(householdId: number): Promise<HouseholdService[]> {
    const res = await this.client.get(`/api/households/${householdId}/services`);
    return res.data;
  }

  async updateService(
    householdId: number,
    serviceId: number,
    data: HouseholdServiceUpdate
  ): Promise<{ status: string }> {
    const res = await this.client.put(`/api/households/${householdId}/services/${serviceId}`, data);
    return res.data;
  }

  // ==========================================================================
  // INDIVIDUAL SURVIVOR NEEDS
  // ==========================================================================

  async createNeed(householdId: number, data: SurvivorNeedCreate): Promise<{ id: number; status: string }> {
    const res = await this.client.post(`/api/households/${householdId}/needs`, data);
    return res.data;
  }

  async listNeeds(householdId: number, params?: {
    household_member_id?: number;
    need_status?: NeedStatus;
    scope?: 'individual' | 'household';
  }): Promise<SurvivorNeed[]> {
    const res = await this.client.get(`/api/households/${householdId}/needs`, { params });
    return res.data;
  }

  async listMemberNeeds(householdId: number, memberId: number): Promise<SurvivorNeed[]> {
    const res = await this.client.get(`/api/households/${householdId}/members/${memberId}/needs`);
    return res.data;
  }

  async updateNeed(householdId: number, needId: number, data: SurvivorNeedUpdate): Promise<{ status: string }> {
    const res = await this.client.put(`/api/households/${householdId}/needs/${needId}`, data);
    return res.data;
  }

  async deleteNeed(householdId: number, needId: number): Promise<{ status: string }> {
    const res = await this.client.delete(`/api/households/${householdId}/needs/${needId}`);
    return res.data;
  }

  // ==========================================================================
  // FOLLOW-UPS / NEXT STEPS
  // ==========================================================================

  async createFollowUp(householdId: number, data: FollowUpCreate): Promise<{ id: number; status: string }> {
    const res = await this.client.post(`/api/households/${householdId}/follow-ups`, data);
    return res.data;
  }

  async listFollowUps(householdId: number, status?: FollowUpStatus): Promise<FollowUp[]> {
    const res = await this.client.get(`/api/households/${householdId}/follow-ups`, {
      params: status ? { status } : undefined,
    });
    return res.data;
  }

  async getNextFollowUp(householdId: number): Promise<FollowUp | null> {
    const res = await this.client.get(`/api/households/${householdId}/follow-ups/next`);
    return res.data;
  }

  async updateFollowUp(householdId: number, followUpId: number, data: FollowUpUpdate): Promise<{ status: string }> {
    const res = await this.client.put(`/api/households/${householdId}/follow-ups/${followUpId}`, data);
    return res.data;
  }

  // ==========================================================================
  // TIMELINE
  // ==========================================================================

  async getTimeline(householdId: number): Promise<TimelineEntry[]> {
    const res = await this.client.get(`/api/households/${householdId}/timeline`);
    return res.data;
  }
}

// Singleton export
export const householdsAPI = new HouseholdsAPI();
